import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faUserPlus, faUserShield } from '@fortawesome/free-solid-svg-icons';
import permissionService from '../../../services/permissionService';
import Table from '../../../components/Common/Table/Table';
import Heading from '../../../components/Common/Heading/Heading';
import ActionButtons from '../../../components/Common/ActionButtons/ActionButtons';
import SkeletonLoader from '../../../components/SkeletonLoader/SkeletonLoader';
import ListScreenSearchBar from '../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import './RoleList.css';
import '../../../components/Common/ListScreenPageLayout/listScreenPage.css';

const RoleList = () => {
  const navigate = useNavigate();
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);
  const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });

  const fetchRoles = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionService.getRoles();

      if (response.success) {
        setRoles(response.data || []);
      } else {
        setError(response.message || 'Failed to fetch roles');
        toast.error(response.message || 'Failed to fetch roles');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to fetch roles';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRoles();
  }, [fetchRoles]);

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setCurrentPage(1);
  };

  const handleAddRole = () => {
    navigate('/permission/roles/add');
  };

  const handleEditRole = (roleId) => {
    navigate(`/permission/roles/edit/${roleId}`);
  };

  const handleAssignPermission = (roleId) => {
    navigate(`/permission/roles/${roleId}/assign`);
  };

  // Filter & Sort roles
  const filteredRoles = useMemo(() => {
    let result = [...roles];
    if (searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(
        (role) =>
          role.id?.toString().includes(searchLower) ||
          role.role_name?.toLowerCase().includes(searchLower)
      );
    }

    if (sortConfig.key) {
      const mul = sortConfig.direction === 'asc' ? 1 : -1;
      result.sort((a, b) => {
        if (sortConfig.key === 'id') {
          return mul * ((Number(a.id) || 0) - (Number(b.id) || 0));
        }
        return mul * String(a[sortConfig.key] || '').localeCompare(String(b[sortConfig.key] || ''), undefined, { sensitivity: 'base' });
      });
    }

    return result;
  }, [roles, searchTerm, sortConfig]);

  const totalFiltered = filteredRoles.length;
  const effectiveItemsPerPage = itemsPerPage === 9999 ? Math.max(totalFiltered, 1) : itemsPerPage;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / effectiveItemsPerPage));
  const paginatedRoles = useMemo(() => {
    if (itemsPerPage === 9999) return filteredRoles;
    const startIndex = (currentPage - 1) * effectiveItemsPerPage;
    return filteredRoles.slice(startIndex, startIndex + effectiveItemsPerPage);
  }, [filteredRoles, currentPage, effectiveItemsPerPage, itemsPerPage]);

  const columns = [
    {
      key: 'id',
      title: 'Role Id',
      sortable: true,
      className: 'reusable-table-centered-col',
      render: (row) => row.id ?? 'N/A',
    },
    {
      key: 'role_name',
      title: 'Role Name',
      sortable: true,
      render: (row) => (
        <span className="role-name-text">
          <FontAwesomeIcon icon={faUserShield} className="me-2 text-muted" />
          {row.role_name || 'N/A'}
        </span>
      ),
    },
    {
      key: 'action',
      title: 'Action',
      className: 'reusable-table-centered-col',
      render: (row) => (
        <div className="role-action-buttons-wrap">
          <ActionButtons
            onEdit={() => handleEditRole(row.id)}
            hideView
            hideDelete
          />
          {Number(row.id) !== 1 && (
            <button
              type="button"
              onClick={() => handleAssignPermission(row.id)}
              className="role-assign-btn"
              title="Assign Permissions"
            >
              <FontAwesomeIcon icon={faUserPlus} /> Assign Permissions
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="role-list-page list-screen-page-container">
      {/* Header Top Bar */}
      <div className="role-list-header-flex list-screen-top-bar">
        <div className="list-screen-title-section">
          <Heading title="Role List" level="h2" className="role-list-title list-screen-title-text" />
        </div>
        <div className="role-list-header-actions list-screen-actions">
          <ListScreenSearchBar
            value={searchTerm}
            onChange={(value) => {
              setSearchTerm(value);
              setCurrentPage(1);
            }}
            inputId="role-list-search"
          />
          <button onClick={handleAddRole} className="role-list-add-btn list-screen-add-btn">
            <FontAwesomeIcon icon={faPlus} /> Add Role
          </button>
        </div>
      </div>

      {error && (
        <div className="role-list-error-message">
          <p>{error}</p>
          <button onClick={fetchRoles} className="role-list-retry-btn">Retry</button>
        </div>
      )}

      {/* Enterprise Card Container */}
      <div className="role-list-container">
        <Table
          columns={columns}
          data={paginatedRoles}
          sortBy={sortConfig.key}
          sortOrder={sortConfig.direction === 'asc' ? 'asc' : 'desc'}
          onSort={requestSort}
          isLoading={loading}
          loadingMode="skeleton"
          skeletonRows={Math.min(effectiveItemsPerPage, 10)}
          emptyMessage={searchTerm ? 'No roles found matching your search' : 'No roles found'}
        />

        {!loading && totalFiltered > 0 && (
          <ListScreenPaginationBar
            entriesValue={itemsPerPage === 9999 ? 'all' : String(itemsPerPage)}
            onEntriesChange={(val) => {
              setItemsPerPage(val === 'all' ? 9999 : Number(val));
              setCurrentPage(1);
            }}
            showPageSelector
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>
    </div>
  );
};

export default RoleList;
